// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import './NavigationPanel.css';
import { useFuel } from '../contexts/FuelContext';
import { useCredits } from '../contexts/CreditsContext';
import { useShipHealth } from '../contexts/ShipHealthContext';
import { useUpgrades } from '../contexts/UpgradesContext';
import { useUniverse } from '../contexts/UniverseContext';
import * as NavUtils from '../utils/game/NavigationUtils';
import * as WarpCalc from '../utils/game/WarpCalculations';
import GameConfig from '../config/GameConfig';
import EventBus, { EVENT_TYPES } from '../utils/system/EventBus';
import GameAlert from '../utils/ui/GameAlertUtils';
import STORAGE_KEYS from '../utils/system/STORAGE_KEYS';
import SoundEffects from '../utils/system/SoundEffects';
import achievementManager from '../utils/system/AchievementManager';
import { modifyReputation } from '../utils/game/ReputationManager';
import StarMap from './StarMap';
import FuelGauge from './FuelGauge';
import WarpControls from './WarpControls';
import WarpEffect from './WarpEffect';
import GateWarpEffect from './GateWarpEffect';
import CourseModal from './CourseModal';
import GravityWellEffect from './GravityWellEffect';
import EnhancedWarpEventManager from './EnhancedWarpEventManager';
import ArrivalScreen from './ArrivalScreen';
import DockScreen from './DockScreen';
import TowAnimation from './TowAnimation';
import EmergencyFuelModal from './EmergencyFuelModal';

// Import extracted logic hooks
import { useWarpTravelLogic } from '../hooks/navigation/WarpTravelLogic';
import { useLocationManager } from './LocationManager';
import { useScanningLogic } from '../hooks/navigation/ScanningLogic';
import { useEventHandlers } from '../hooks/navigation/EventHandlers';
import { useUniverseValidation } from '../hooks/useUniverseValidation';
import { useCourseCalculation } from '../hooks/navigation/useCourseCalculation';
import { useOrbitManagement } from '../hooks/navigation/useOrbitManagement';
import { useWarpGateTravel } from '../hooks/navigation/useWarpGateTravel';
import { useTowService } from '../hooks/navigation/useTowService';
// Lazy load large modal components
const ExpandedStarMap = lazy(() => import('./ExpandedStarMap'));
const SystemScanner = lazy(() => import('./SystemScanner'));
const PlanetExploration = lazy(() => import('./PlanetExploration'));

const NavigationPanel = () => {
  // Universe context
  const { 
    currentUniverse,
    getCurrentLocations, 
    getCurrentPositions,
    getCurrentUniverseInfo,
    getCurrentUniverseData,
    isWarpGate,
    travelThroughGate
  } = useUniverse();
  
  // Universe validation (dev mode only)
  const { validateOnEnter } = useUniverseValidation();  
  // Context API
  const { fuel: antimatterFuel, maxFuel, addFuel, removeFuel, setFuel: setFuelDirect, refuelToFull, getFuelPercent } = useFuel();
  const { addCredits, removeCredits, credits } = useCredits();
  const { getSystemHealth, damageSystem } = useShipHealth();
  const { getFuelEfficiencyBonus } = useUpgrades();
  
  // Core state
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [selectedWarpFactor, setSelectedWarpFactor] = useState(null);
  const [activeWarpFactor, setActiveWarpFactor] = useState(null); // Current warp during travel
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [courseProgress, setCourseProgress] = useState(0);
  const [autoEngageCountdown, setAutoEngageCountdown] = useState(null);
  
  // Listen for mission completion events
  useEffect(() => {
    const handleMissionComplete = (data) => {
      addCredits(data.credits);
      if (data.reputation && data.reputation > 0) {
        const factionId = data.mission?.faction || 'galactic_federation';
        modifyReputation(factionId, data.reputation, 'mission_complete');
      }
    };

    EventBus.on(EVENT_TYPES.MISSION_COMPLETE, handleMissionComplete);
    return () => EventBus.off('MISSION_COMPLETE', handleMissionComplete);
  }, [addCredits]);

  // Crew relationship change notifications
  useEffect(() => {
    const handleRelationshipChange = (data) => {
      GameAlert.info(data.message);
    };

    const handleRelationshipMilestone = (data) => {
      GameAlert.success(data.message);
    };

    EventBus.on(EVENT_TYPES.CREW_RELATIONSHIP_CHANGE, handleRelationshipChange);
    EventBus.on(EVENT_TYPES.CREW_RELATIONSHIP_MILESTONE, handleRelationshipMilestone);
    
    return () => {
      EventBus.off('CREW_RELATIONSHIP_CHANGE', handleRelationshipChange);
      EventBus.off('CREW_RELATIONSHIP_MILESTONE', handleRelationshipMilestone);
    };
  }, []);

  // Crew relationship time decay - runs every 5 minutes
  useEffect(() => {
    const decayInterval = setInterval(() => {
      if (window.CrewRelationshipManager && window.CrewManager) {
        const crew = window.CrewManager.getAllCrew();
        const crewIds = crew.map(c => c.id);
        window.CrewRelationshipManager.applyTimeDecay(crewIds);
      }
    }, (GameConfig.crewRelationships?.timeDecay?.intervalMinutes || 5) * 60 * 1000);

    return () => clearInterval(decayInterval);
  }, []);

  // Initialize navigation mode from localStorage

  // Track system visits for achievements and recent history
  useEffect(() => {
    if (currentLocation && currentLocation.id !== undefined && currentLocation.id !== -1) {
      achievementManager.visitSystem(currentLocation.id);
      // Update recent systems list (most recent first, max 10)
      try {
        const recent = JSON.parse(localStorage.getItem(STORAGE_KEYS.RECENT_SYSTEMS) || '[]');
        const filtered = recent.filter(id => id !== currentLocation.id);
        filtered.unshift(currentLocation.id);
        localStorage.setItem(STORAGE_KEYS.RECENT_SYSTEMS, JSON.stringify(filtered.slice(0, 10)));
      } catch (e) { /* ignore */ }
    }
  }, [currentLocation]);

  // Track universe changes for achievements
  useEffect(() => {
    if (currentUniverse) {
      achievementManager.visitUniverse(currentUniverse);
    }
  }, [currentUniverse]);
  const [navigationMode, setNavigationMode] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.NAVIGATION_MODE);
    return saved || 'AUTO';
  });
  
  // Initialize discovered systems from localStorage
  const [discoveredSystems, setDiscoveredSystems] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.DISCOVERED_SYSTEMS);
    if (saved) {
      try {
        return new Set(JSON.parse(saved));
      } catch (error) {
        console.error('[NavigationPanel] Failed to parse discovered systems:', error);
      }
    }
    return new Set();
  });
  
  // Warp and travel state
  const [showWarpEffect, setShowWarpEffect] = useState(false);
  const [showGateWarpEffect, setShowGateWarpEffect] = useState(false);
  const [gateDestinationUniverseId, setGateDestinationUniverseId] = useState(null);
  const [warpElements, setWarpElements] = useState([]);
  const [warpDisengaging, setWarpDisengaging] = useState(false);
  const [warpTravelTime, setWarpTravelTime] = useState(0);
  const [warpTravelProgress, setWarpTravelProgress] = useState(0);
  const [hasArrived, setHasArrived] = useState(false);
  const [showArrivalScreen, setShowArrivalScreen] = useState(false);
  const [showOrbitScreen, setShowOrbitScreen] = useState(false);
  const [planetStars, setPlanetStars] = useState([]);
  const [fuelDepleted, setFuelDepleted] = useState(false);
  const [towOfferData, setTowOfferData] = useState(null);
  const [showDockingSequence, setShowDockingSequence] = useState(false);
  const [dockingProgress, setDockingProgress] = useState(0);
  const [inCombat, setInCombat] = useState(false);
  const [expandedMap, setExpandedMap] = useState(false);
  const [hoveredSystem, setHoveredSystem] = useState(null);
  const [warpPaused, setWarpPaused] = useState(false);
  const [showPlanetExploration, setShowPlanetExploration] = useState(false);
  const [showGravityEffect, setShowGravityEffect] = useState(false);
  const [fuelConsumptionRate, setFuelConsumptionRate] = useState(0);
  const [showTowAnimation, setShowTowAnimation] = useState(false);
  const [towDestination, setTowDestination] = useState(null);
  const [activeEvent, setActiveEvent] = useState(null);
  const [showEmergencyFuel, setShowEmergencyFuel] = useState(false);
  
  // Refs
  const fuelRef = useRef(antimatterFuel);
  const warpPausedRef = useRef(false);
  const intervalRef = useRef(null);
  const autoEngageTimerRef = useRef(null);
  
  // Update fuel ref
  React.useEffect(() => {
    fuelRef.current = antimatterFuel;
  }, [antimatterFuel]);
  
  // Update warpPausedRef when warpPaused changes
  React.useEffect(() => {
    warpPausedRef.current = warpPaused;
  }, [warpPaused]);
  
  // Get star systems and positions from universe context
  const starSystems = getCurrentLocations();
  const distancePositions = getCurrentPositions();
  
  // Helper functions
  const getDisplayDistance = (system) => {
    if (!system || !currentLocation) return 0;
    return NavUtils.calculateDistance(currentLocation, system, distancePositions);
  };

  const getDefaultWarpFactor = () => {
    if (navigationMode === 'AUTO' && selectedSystem) {
      const actualDistance = getDisplayDistance(selectedSystem);
      return WarpCalc.calculateOptimalWarp(actualDistance, antimatterFuel, maxFuel, getFuelEfficiencyBonus());
    } else if (navigationMode === 'EMERGENCY') {
      return 8;
    } else {
      return 1;
    }
  };

  const generatePlanetStars = () => NavUtils.generatePlanetStars(200);
  
  // Extract logic into custom hooks
  const locationManager = useLocationManager({
    currentUniverse,
    getCurrentLocations,
    getCurrentPositions,
    setCurrentLocation
  });
  
  const scanningLogic = useScanningLogic({
    selectedSystem,
    currentLocation,
    antimatterFuel,
    getSystemHealth,
    getDisplayDistance,
    getCurrentUniverseData,
    addFuel,
    addCredits,
    removeFuel,
    setActiveEvent
  });
  
  const warpTravelLogic = useWarpTravelLogic({
    selectedSystem,
    currentLocation,
    distancePositions,
    selectedWarpFactor,
    getDefaultWarpFactor,
    getFuelEfficiencyBonus,
    antimatterFuel,
    maxFuel,
    starSystems,
    navigationMode,
    fuelRef,
    warpPausedRef,
    setCurrentLocation,
    setHasArrived,
    setShowArrivalScreen,
    setShowWarpEffect,
    setWarpDisengaging,
    setWarpElements,
    setWarpTravelTime,
    setWarpTravelProgress,
    setFuelDepleted,
    setFuelConsumptionRate,
    setActiveEvent,
    removeFuel,
    enrichLocationWithCoordinates: locationManager.enrichLocationWithCoordinates
  });
  
  const eventHandlers = useEventHandlers({
    setActiveEvent,
    setInCombat,
    setTowOfferData,
    setTowDestination,
    setShowTowAnimation,
    showWarpEffect,
    inCombat,
    selectedSystem,
    warpTravelTime,
    warpTravelProgress,
    currentLocation,
    starSystems,
    distancePositions,
    towOfferData,
    fuelDepleted,
    startWarpTimer: warpTravelLogic.startWarpTimer,
    warpTimerRef: warpTravelLogic.warpTimerRef
  });

  // Enrich star systems with discovered state
  const enrichedStarSystems = React.useMemo(() => {
    return starSystems.map(system => ({
      ...system,
      discovered: discoveredSystems.has(system.id)
    }));
  }, [starSystems, discoveredSystems]);

  // Persist location changes
  React.useEffect(() => {
    locationManager.persistLocation(currentLocation);
  }, [currentLocation, currentUniverse, locationManager]);

  // Persist discovered systems to localStorage
  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.DISCOVERED_SYSTEMS, JSON.stringify(Array.from(discoveredSystems)));
  }, [discoveredSystems]);
  
  // Persist navigation mode to localStorage
  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.NAVIGATION_MODE, navigationMode);
  }, [navigationMode]);

  // Ensure currentLocation always has coordinates and universeId
  React.useEffect(() => {
    if (currentLocation && !currentLocation.x && currentLocation.id !== -1) {
      const enriched = locationManager.enrichLocationWithCoordinates(currentLocation);
      if (enriched && (enriched.x !== currentLocation.x || enriched.universeId !== currentLocation.universeId)) {
        setCurrentLocation(enriched);
      }
    }
  }, [currentLocation, currentUniverse, locationManager]);

  // Handle scan credits bonus when scan results change
  React.useEffect(() => {
    if (scanningLogic.scanResults && scanningLogic.scanResults.creditsBonus > 0 && !scanningLogic.scanResults.isRescan) {
      const timer = setTimeout(() => {
        addCredits(scanningLogic.scanResults.creditsBonus, 'scan discovery');
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [scanningLogic.scanResults, addCredits]);

  // Pre-generate warp elements for better performance
  const generateWarpElements = () => {
    const currentWarp = selectedWarpFactor || getDefaultWarpFactor();
    return NavUtils.generateWarpElements(currentWarp);
  };

  const courseCalculation = useCourseCalculation({
    selectedSystem,
    currentLocation,
    selectedWarpFactor,
    navigationMode,
    antimatterFuel,
    maxFuel,
    starSystems,
    courseProgress,
    showCourseModal,
    intervalRef,
    autoEngageTimerRef,
    getFuelEfficiencyBonus,
    getDisplayDistance,
    getDefaultWarpFactor,
    generateWarpElements,
    setShowCourseModal,
    setCourseProgress,
    setShowOrbitScreen,
    setActiveWarpFactor,
    setShowWarpEffect,
    setWarpElements,
    setNavigationMode,
    setSelectedWarpFactor,
    setExpandedMap,
    setShowEmergencyFuel,
    setAutoEngageCountdown,
    startWarpTimer: warpTravelLogic.startWarpTimer
  });

  const orbitManagement = useOrbitManagement({
    selectedSystem,
    warpTimerRef: warpTravelLogic.warpTimerRef,
    enrichLocationWithCoordinates: locationManager.enrichLocationWithCoordinates,
    setCurrentLocation,
    setShowArrivalScreen,
    setShowOrbitScreen,
    setShowWarpEffect,
    setWarpDisengaging,
    setWarpElements,
    setWarpTravelTime,
    setWarpTravelProgress,
    setDockingProgress,
    setShowDockingSequence,
    setHasArrived,
    setActiveEvent,
    setExpandedMap
  });

  const towService = useTowService({
    towOfferData,
    enrichLocationWithCoordinates: locationManager.enrichLocationWithCoordinates,
    generatePlanetStars,
    setCurrentLocation,
    setSelectedSystem,
    setFuelDirect,
    setFuelDepleted,
    setTowOfferData,
    setTowDestination,
    setShowTowAnimation,
    setShowArrivalScreen,
    setShowOrbitScreen,
    setHasArrived,
    setPlanetStars,
    setActiveEvent
  });

  const warpGateTravel = useWarpGateTravel({
    antimatterFuel,
    maxFuel,
    currentUniverse,
    discoveredSystems,
    travelThroughGate,
    validateOnEnter,
    enrichLocationWithCoordinates: locationManager.enrichLocationWithCoordinates,
    generatePlanetStars,
    setCurrentLocation,
    setSelectedSystem,
    setDiscoveredSystems,
    setShowOrbitScreen,
    setShowGateWarpEffect,
    setGateDestinationUniverseId,
    setExpandedMap,
    setHasArrived,
    setPlanetStars,
    setActiveEvent
  });

  const { setCourse, handleWarpSelection, handleModeChange } = courseCalculation;
  const { handleEnterOrbit, handleLeaveOrbit } = orbitManagement;
  const { handleTowComplete } = towService;
  const { handleGateActivation } = warpGateTravel;

  // Handle dynamic warp factor change during travel
  const handleWarpFactorChangeDuringTravel = (newWarpFactor) => {
    setActiveWarpFactor(newWarpFactor);
    setSelectedWarpFactor(newWarpFactor);
    
    // Recalculate travel time and fuel consumption
    const actualDistance = getDisplayDistance(selectedSystem);
    const remainingProgress = 100 - warpTravelProgress;
    const distanceRemaining = actualDistance * (remainingProgress / 100);
    
    const newTravelTime = WarpCalc.calculateTravelTime(distanceRemaining, newWarpFactor);
    const upgradeBonuses = { fuelEfficiency: getFuelEfficiencyBonus() };
    const newFuelNeeded = NavUtils.calculateFuelNeeded(distanceRemaining, newWarpFactor, upgradeBonuses.fuelEfficiency);
    const newFuelConsumptionRate = newFuelNeeded / newTravelTime;

    // Update warp travel state
    setWarpTravelTime(newTravelTime);
    setFuelConsumptionRate(newFuelConsumptionRate);
    
    // Restart warp timer with new parameters
    if (warpTravelLogic.warpTimerRef.current) {
      clearInterval(warpTravelLogic.warpTimerRef.current);
    }
    
    // Continue warp travel with new speed
    warpTravelLogic.startWarpTimer(newTravelTime, false);
  };

  return (
    <div className="navigation-panel">
      <div className="nav-header glow">
        ◊ STELLAR CARTOGRAPHY ◊
      </div>

      <EnhancedWarpEventManager
        fuel={antimatterFuel}
        removeFuel={removeFuel}
        addCredits={addCredits}
        removeCredits={removeCredits}
        damageSystem={damageSystem}
        getSystemHealth={getSystemHealth}
        selectedWarpFactor={selectedWarpFactor}
        onSetWarpFactor={setSelectedWarpFactor}
        onGravityEffect={setShowGravityEffect}
        refuelToFull={refuelToFull}
        fuelDepleted={fuelDepleted}
        inCombat={inCombat}
        currentLocation={currentLocation}
        warpTravelProgress={warpTravelProgress}
        warpTravelTime={warpTravelTime}
        onCombatInterrupt={(data) => {
          // Combat interrupt is handled by WeaponsPanel via combatEncounter event
        }}
        getDisplayDistance={getDisplayDistance}
        getDefaultWarpFactor={getDefaultWarpFactor}
        credits={credits}
        setWarpPaused={setWarpPaused}
      />

      <div className="nav-content">
        <div className="nav-content-main">
          <StarMap
            starSystems={enrichedStarSystems}
            distancePositions={distancePositions}
            currentLocation={currentLocation}
            selectedSystem={selectedSystem}
            scannedSystems={scanningLogic.scannedSystems}
            navigationMode={navigationMode}
            onSystemSelect={(system) => {
              if (isWarpGate(system)) {
                if (!discoveredSystems.has(system.id)) {
                  setDiscoveredSystems(prev => new Set([...prev, system.id]));
                }
              }
              setSelectedSystem(system);
              if (activeEvent && activeEvent.type === 'WARNING') {
                setActiveEvent(null);
              }
              if (navigationMode === 'AUTO') {
                setSelectedWarpFactor(null);
              }
            }}
            onSetCourse={setCourse}
            onStartScan={scanningLogic.startScan}
            onExpandMap={() => setExpandedMap(true)}
            getDisplayDistance={getDisplayDistance}
          />
        </div>

        <div className="nav-content-bottom">
          <FuelGauge fuelLevel={antimatterFuel} />
          
          <WarpControls
            selectedWarpFactor={selectedWarpFactor}
            navigationMode={navigationMode}
            onWarpSelection={handleWarpSelection}
            onModeChange={handleModeChange}
            getDefaultWarpFactor={getDefaultWarpFactor}
          />
        </div>
      </div>

      <CourseModal
        show={showCourseModal}
        selectedSystem={selectedSystem}
        courseProgress={courseProgress}
        navigationMode={navigationMode}
        selectedWarpFactor={selectedWarpFactor}
        antimatterFuel={antimatterFuel}
        getDisplayDistance={getDisplayDistance}
        getDefaultWarpFactor={getDefaultWarpFactor}
        calculateTravelTime={(distance, warpFactor) => WarpCalc.calculateTravelTime(distance, warpFactor)}
        currentLocation={currentLocation}
        starSystems={starSystems}
        distancePositions={distancePositions}
        onSelectAlternative={(alternativeSystem) => {
          setSelectedSystem(alternativeSystem);
          setShowCourseModal(false);
          setCourseProgress(0);
          setTimeout(() => setCourse(), 100);
        }}
        onConfirm={(newWarpFactor, isRisky = false) => {
          const currentWarp = newWarpFactor || selectedWarpFactor || getDefaultWarpFactor();
          if (newWarpFactor && newWarpFactor !== selectedWarpFactor) {
            setSelectedWarpFactor(newWarpFactor);
          }
          const actualDistance = getDisplayDistance(selectedSystem);
          const travelTime = WarpCalc.calculateTravelTime(actualDistance, currentWarp);
          
          setShowCourseModal(false);
          setCourseProgress(0);
          setActiveWarpFactor(currentWarp); // Set BEFORE showing warp effect
          setShowWarpEffect(true);
          EventBus.emit(EVENT_TYPES.WARP_ENGAGED);
          warpTravelLogic.startWarpTimer(travelTime, isRisky);
        }}
        autoEngageCountdown={autoEngageCountdown}
        onCancel={() => {
          setShowCourseModal(false);
          setCourseProgress(0);
          setAutoEngageCountdown(null);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
        }}
      />
      
      {!showOrbitScreen && (
        <Suspense fallback={<div style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          color: '#00ffff', fontSize: '1.2em', fontFamily: 'Orbitron, sans-serif'
        }}>Loading Star Map...</div>}>
          <ExpandedStarMap
            show={expandedMap}
            universeInfo={getCurrentUniverseInfo()}
            starSystems={enrichedStarSystems}
            distancePositions={distancePositions}
            currentLocation={currentLocation}
            selectedSystem={selectedSystem}
            scannedSystems={scanningLogic.scannedSystems}
            hoveredSystem={hoveredSystem}
            antimatterFuel={antimatterFuel}
            selectedWarpFactor={selectedWarpFactor}
            getDisplayDistance={getDisplayDistance}
            getDefaultWarpFactor={getDefaultWarpFactor}
            onSystemSelect={(system) => {
              if (isWarpGate(system)) {
                if (!discoveredSystems.has(system.id)) {
                  setDiscoveredSystems(prev => new Set([...prev, system.id]));
                }
              }
              setSelectedSystem(system);
              setHoveredSystem(null);
              if (activeEvent && activeEvent.type === 'WARNING') {
                setActiveEvent(null);
              }
            }}
            onSystemHover={setHoveredSystem}
            onClose={() => setExpandedMap(false)}
            onSetCourse={setCourse}
            onStartScan={scanningLogic.startScan}
            onStrandedClick={eventHandlers.handleStrandedClick}
            onReenterOrbit={() => {
              setExpandedMap(false);
              setShowOrbitScreen(true);
              EventBus.emit(EVENT_TYPES.ENTER_ORBIT);
            }}
          />
        </Suspense>
      )}

      <Suspense fallback={<div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        color: '#00ffff', fontSize: '1.2em', fontFamily: 'Orbitron, sans-serif'
      }}>Loading Scanner...</div>}>
        <SystemScanner
          showScanModal={scanningLogic.showScanModal}
          scanProgress={scanningLogic.scanProgress}
          selectedSystem={selectedSystem}
          scanResults={scanningLogic.scanResults}
          onClose={scanningLogic.closeScanModal}
        />
      </Suspense>
      
      <Suspense fallback={<div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        color: '#00ffff', fontSize: '1.2em', fontFamily: 'Orbitron, sans-serif'
      }}>Loading Exploration...</div>}>
        <PlanetExploration
          isOpen={showPlanetExploration}
          onClose={() => setShowPlanetExploration(false)}
          planetData={currentLocation}
          onExplorationComplete={(result) => {
          }}
        />
      </Suspense>
      
      {showGravityEffect && (
        <GravityWellEffect 
          onComplete={() => setShowGravityEffect(false)}
        />
      )}
      
      {showWarpEffect && (
        <WarpEffect
          show={showWarpEffect}
          warpDisengaging={warpDisengaging}
          warpElements={warpElements}
          hasArrived={hasArrived}
          planetStars={planetStars}
          warpTravelProgress={warpTravelProgress}
          warpTravelTime={warpTravelTime}
          selectedSystem={selectedSystem}
          antimatterFuel={antimatterFuel}
          fuelConsumptionRate={fuelConsumptionRate}
          maxAntimatterFuel={maxFuel}
          fuelPercentage={getFuelPercent()}
          currentWarpFactor={activeWarpFactor || selectedWarpFactor || getDefaultWarpFactor()}
          onWarpFactorChange={handleWarpFactorChangeDuringTravel}
          distanceRemaining={selectedSystem ? getDisplayDistance(selectedSystem) * ((100 - warpTravelProgress) / 100) : 0}
          fuelEfficiencyBonus={getFuelEfficiencyBonus()}
          onSkip={warpTravelLogic.skipWarp}
          onDisengage={() => {
            const progressPercent = warpTravelProgress / 100;
            const distanceTraveled = getDisplayDistance(selectedSystem) * progressPercent;
            
            if (warpTravelProgress < 100 && warpTravelProgress > 0) {
              const currentPos = distancePositions[currentLocation.id] || { x: 20, y: 50, z: 0 };
              const destPos = distancePositions[selectedSystem.id] || { x: 80, y: 50, z: 0 };
              
              const deepSpaceLocation = {
                id: -1,
                name: `DEEP SPACE (${distanceTraveled.toFixed(2)} LY from ${currentLocation.name})`,
                threat: 'UNKNOWN',
                resources: 'NONE',
                x: currentPos.x + (destPos.x - currentPos.x) * progressPercent,
                y: currentPos.y + (destPos.y - currentPos.y) * progressPercent,
                z: (currentPos.z || 0) + ((destPos.z || 0) - (currentPos.z || 0)) * progressPercent
              };
              setCurrentLocation(deepSpaceLocation);
            }
            
            if (warpTravelLogic.warpTimerRef.current) {
              clearInterval(warpTravelLogic.warpTimerRef.current);
              warpTravelLogic.warpTimerRef.current = null;
            }
            
            setWarpDisengaging(true);
            SoundEffects.stop('warp_travel');
            setTimeout(() => {
              setShowWarpEffect(false);
              setWarpElements([]);
              setWarpDisengaging(false);
              setWarpTravelTime(0);
              setWarpTravelProgress(0);
              setHasArrived(false);
            }, 2000);
          }}
        />
      )}
      
      {showGateWarpEffect && (
        <GateWarpEffect duration={GameConfig.warp.gateTravelTime * 1000} destinationUniverseId={gateDestinationUniverseId} />
      )}
      
      {showArrivalScreen && selectedSystem && (
        <ArrivalScreen 
          location={selectedSystem}
          onEnterOrbit={handleEnterOrbit}
          type={selectedSystem.type}
        />
      )}
      
      {/* Docking Sequence for Stations */}
      {showDockingSequence && selectedSystem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.95)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          fontFamily: 'Orbitron, sans-serif'
        }}>
          <div style={{
            fontSize: '3em',
            fontWeight: 900,
            color: '#00ffff',
            textShadow: '0 0 20px #00ffff, 0 0 40px #00ffff',
            marginBottom: '40px',
            letterSpacing: '4px',
            animation: 'pulse 2s ease-in-out infinite'
          }}>
            DOCKING IN PROGRESS
          </div>
          
          <div style={{
            fontSize: '1.5em',
            color: '#88ccff',
            marginBottom: '30px',
            textAlign: 'center'
          }}>
            {selectedSystem.name}
          </div>
          
          {/* Progress Bar */}
          <div style={{
            width: '500px',
            height: '30px',
            background: 'rgba(0, 0, 0, 0.5)',
            border: '2px solid #00ffff',
            borderRadius: '15px',
            overflow: 'hidden',
            boxShadow: '0 0 20px rgba(0, 255, 255, 0.3)',
            position: 'relative'
          }}>
            <div style={{
              width: `${dockingProgress}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #00ffff, #00aaff)',
              transition: 'width 0.05s linear',
              boxShadow: '0 0 20px rgba(0, 255, 255, 0.8)'
            }} />
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.9em',
              textShadow: '0 0 5px #000'
            }}>
              {Math.floor(dockingProgress)}%
            </div>
          </div>
          
          <div style={{
            marginTop: '30px',
            fontSize: '1em',
            color: '#ffaa00',
            fontStyle: 'italic'
          }}>
            Aligning docking clamps...
          </div>
        </div>
      )}
      
      {showOrbitScreen && currentLocation && (
        <DockScreen 
          location={currentLocation}
          onLeave={handleLeaveOrbit}
          type={currentLocation.type || 'planet'}
          onActivateGate={currentLocation.type === 'warpgate' ? handleGateActivation : undefined}
        />
      )}
      
      {showEmergencyFuel && (
        <EmergencyFuelModal
          isOpen={true}
          onClose={() => setShowEmergencyFuel(false)}
          currentFuel={antimatterFuel}
          maxFuel={maxFuel}
          credits={credits}
          onPurchase={(amount, cost) => {
            if (removeCredits(cost)) {
              addFuel(amount);
              setShowEmergencyFuel(false);
              GameAlert.success(`Emergency fuel purchased! +${amount}% fuel`);
            }
          }}
        />
      )}
      
      <TowAnimation
        isActive={showTowAnimation}
        destinationName={towDestination}
        onComplete={handleTowComplete}
      />
    </div>
  );
};

export default NavigationPanel;
